<template>
  <div class="grid-container">
    <app-sidebar />
    <div class="m-1 mt-3 lg:m-3 min-height">
      <div class="back-grid">
        <back-button></back-button>
        <h2 style="text-align: center; font-weight: bold">Feedback</h2>
      </div>
      <br />
      <h5 class="text-align-center pl-1 pr-1 lg:pl-8 lg:pr-8">
        Your feedback is important to us for improving the platform. Please
        provide feedback based on your experience. We are happy to hear from
        you.
      </h5>
      <div class="card card-container">
        <span class="font-color-red">* required field</span>
        <form @submit.prevent="handleSubmission">
          <div>
            <div class="form-group">
              <label for="rating" style="margin-right: 1rem">
                Rating<span class="super font-color-red">*</span>
              </label>
              <!-- Hidden input to keep the rating value -->
              <input type="hidden" v-model="rating" />

              <!-- Unhappy Button -->
              <button
                type="button"
                class="emoji"
                @click="updateRating(0)"
                :class="{ selected: rating === 0 }"
              >
                &#128542;
                <span><br />Unhappy</span>
              </button>

              <!-- Neutral Button -->
              <button
                type="button"
                class="emoji ml-3"
                @click="updateRating(1)"
                :class="{ selected: rating === 1 }"
              >
                &#128528;
                <span><br />Neutral</span>
              </button>

              <!-- Happy Button -->
              <button
                type="button"
                class="emoji ml-3"
                @click="updateRating(2)"
                :class="{ selected: rating === 2 }"
              >
                &#128515;
                <span><br />Happy</span>
              </button>
              <ErrorMessage name="rating" class="error-feedback" />
            </div>

            <br />
            <div
              class="form-group"
              v-if="
                !feedbackFields.find((el) => el.name === 'message')?.disabled
              "
            >
              <label for="message">
                Message<span class="super font-color-red">*</span>
              </label>
              <Field
                name="message"
                type="text"
                as="textarea"
                class="form-control"
                :maxlength="TEXT_MAX_LENGTH"
                @input="onInputWithNotification(TEXT_MAX_LENGTH, $event)"
                style="height: 100px"
              />
              <div class="size-counter">
                <span>{{ message ? message.length : 0 }} / 5000</span>
              </div>
              <ErrorMessage name="message" class="error-feedback" />
            </div>
          </div>

          <br />
          <div style="width: 100%">
            <div class="form-group" style="text-align: center">
              <button
                class="btn btn-primary"
                style="text-align: center"
                :disabled="loading"
              >
                <span
                  v-show="loading"
                  class="spinner-border spinner-border-sm"
                ></span>
                Submit
              </button>
            </div>
          </div>
        </form>

        <div
          v-if="msg"
          class="alert"
          :class="successful ? 'alert-success' : 'alert-danger'"
        >
          {{ msg }}
        </div>
      </div>
      <div class="mt-8" v-if="userFeedback.length > 0">
        <h3 class="text-align-center">Your Feedback</h3>
        <div class="course-item mt-5">
          <div
            class="lg:m-1 card"
            v-for="feedback in userFeedback"
            :key="feedback.id"
          >
            <div
              class="flex flex-direction-column lg:flex-row xl:flex-row justify-content-between align-content-center flex-wrap"
            >
              <div
                style="
                  word-wrap: break-word; /* Ensure long words wrap properly */
                  word-break: break-word; /* Handle unbreakable strings */
                  white-space: pre-wrap; /* Preserve line breaks and whitespace */
                  max-width: 100%; /* Prevent comments from overflowing */
                  overflow-wrap: anywhere; /* Support breaking anywhere in text */
                "
              >
                <b>{{ feedback.message }}</b>
                &nbsp;
                <span v-if="feedback.rating === '0'" title="Unhappy">
                  &#128542;
                </span>
                <span v-else-if="feedback.rating === '1'" title="Neutral">
                  &#128528;
                </span>
                <span v-else-if="feedback.rating === '2'" title="Happy">
                  &#128515;
                </span>
                <br />
                {{ new Date(feedback.createdAt).toDateString() }}
              </div>

              <div
                class="flex flex-grow-0 align-content-center lg:flex-wrap mr-3 leave-comment"
                style="cursor: default"
              >
                <button
                  class="btn btn-primary"
                  @click="
                    (currFeedback = feedback), getFeedbackComments(feedback.id)
                  "
                >
                  Leave a comment
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="User Feedback"
    :style="{ width: '60vw', padding: '20px', backgroundColor: '#fff' }"
    :breakpoints="{ '768px': '90vw' }"
  >
    <!-- Dialog content remains unchanged -->
    <div class="flex flex-direction-row">
      <div class="flex-shrink-1 flex-grow-0">
        <profile-icon />
      </div>
      <span class="ml-3 mt-2 bold-text dark-text-color">
        {{ currentUser.firstName }} {{ currentUser.lastName }}
      </span>
    </div>
    <div class="mt-2" style="font-size: larger" autofocus>
      {{ currFeedback.message }}
    </div>
    <va-divider vertical color="red" />
    <div
      v-for="feedbackComment in feedbackComments"
      v-bind:key="feedbackComment.id"
    >
      <div class="flex flex-direction-row ml-2 overflow-scroll">
        <div class="flex-shrink-1 flex-grow-0">
          <img
            v-if="feedbackComment.user.fileName"
            id="profile-img"
            :src="
              websiteURL + 'profile_picture/' + feedbackComment.user.fileName
            "
            class="profile-icon-card"
          />
          <img
            v-else
            id="profile-img"
            src="//ssl.gstatic.com/accounts/ui/avatar_2x.png"
            class="profile-icon-card"
          />
        </div>
        <div class="mb-2">
          <div class="ml-3 pl-3 mt-1 card comment-card">
            <div class="bold-text dark-text-color">
              {{ feedbackComment.user.firstName }}
              {{ feedbackComment.user.lastName }}
            </div>
            {{ feedbackComment.message }}
          </div>
          <span class="ml-3 pl-1 small">
            {{ getTimeDiff(feedbackComment.createdAt) }}
          </span>
        </div>
      </div>
    </div>
    <va-divider />
    <div class="flex flex-direction-row">
      <div class="flex-shrink-1 flex-grow-0">
        <profile-icon />
      </div>
      <div class="ml-3 mt-2" v-if="!editable" @click="editable = true">
        Add Comment
      </div>
      <div class="ml-3 mt-2" v-else>
        <form @submit.prevent="postComment">
          <div class="flex flex-direction-row">
            <Field name="message" v-slot="{ field }">
              <textarea
                type="text"
                v-bind="field"
                class="form-control border-2"
                placeholder="Add a comment..."
              />
              <ErrorMessage name="message" class="error-feedback" />
            </Field>
            <button class="btn btn-primary mx-4 my-3">Reply</button>
          </div>
        </form>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useForm, useField, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import Dialog from "primevue/dialog";
import AppSidebar from "@/components/user/AppSidebar.vue";
import BackButton from "@/components/user/BackButton.vue";
import ProfileIcon from "@/components/user/ProfileIcon.vue";
import UserService from "../services/user.service.js";
import { useConfigStore } from "@/stores/ConfigStore";
import { useAuthStore } from "@/stores/AuthStore";
import { websiteURL, getTimeDiff, showToast, showErrorToast } from "@/utils";
import { VaDivider } from "vuestic-ui";
import { Feedback, FeedbackComment } from "@/types";

const TEXT_MAX_LENGTH = 5000;

const router = useRouter();
const ConfigStore = useConfigStore();
const AuthStore = useAuthStore();

const feedbackFields = ConfigStore.config.filter(
  (el) => el.table === "feedback"
);
const currentUser = AuthStore.auth;

let loading = ref(false);
let successful = ref(false);
let msg = ref("");
let userFeedback = ref([] as Feedback[]);
let currFeedback = ref({} as Feedback);
let feedbackComments = ref([] as FeedbackComment[]);
let isVisible = ref(false);
let editable = ref(false);

// Define your schema with custom tests
const schema = yup.object().shape({
  rating: yup
    .number()
    .required("Rating is required!")
    .min(0, "Rating must be at least 0!"),
  message: yup
    .string()
    .required("Message is required!")
    .min(10, "Message must be at least 10 characters!")
    .max(
      TEXT_MAX_LENGTH,
      `Message cannot exceed ${TEXT_MAX_LENGTH} characters!`
    ),
});

const onInputWithNotification = (maxLength, event) => {
  const value = event.target.value;
  if (value.length >= maxLength) {
    event.target.value = value.substring(0, maxLength); // Prevent further input
    showErrorToast(`Maximum character limit of ${maxLength} reached.`);
  }
};

// Initialize the form
const { handleSubmit, setFieldValue, values, meta, errors } = useForm({
  initialValues: {
    rating: null,
    message: "",
  },
});

// Initialize fields
const { value: rating, errorMessage: ratingError } = useField("rating");
const { value: message, errorMessage: messageError } = useField("message");

// Function to update rating
const updateRating = (value) => {
  rating.value = value;
  setFieldValue("rating", value);
};

// Handle form submission
const handleSubmission = handleSubmit(async (data, { setErrors }) => {
  loading.value = true;

  try {
    // Validate fields manually
    await schema.validate(data, { abortEarly: false });

    // Proceed with submission if validation passes
    UserService.saveFeedback(data).then(
      (response) => {
        msg.value = response.message;
        successful.value = true;
        loading.value = false;
        showToast("Your Feedback has been successfully submitted.");
        router.push({ name: "home" });
      },
      (error) => {
        const errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          error.toString();
        msg.value = errorMessage;
        successful.value = false;
        loading.value = false;
        showErrorToast(`Failed to submit feedback: ${errorMessage}`);
      }
    );
  } catch (validationError) {
    // Handle validation errors
    const fieldErrors = {};
    validationError.inner.forEach((error) => {
      fieldErrors[error.path] = error.message;
      showErrorToast(error.message); // Toast notifications will now show as intended
    });

    setErrors(fieldErrors);
    loading.value = false;
  }
});

// Fetch user feedback
const getUserFeedback = (userId) => {
  UserService.getUserFeedback(userId).then(
    (response) => {
      console.log(response);
      userFeedback.value = response.data;
    },
    (error) => {
      msg.value =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
    }
  );
};

// Fetch feedback comments
const getFeedbackComments = (feedbackId) => {
  UserService.getFeedbackComments(feedbackId).then(
    (response) => {
      console.log(response);
      feedbackComments.value = response.data;
      isVisible.value = true;
    },
    (error) => {
      msg.value =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
    }
  );
};

// Post a comment
const postComment = handleSubmit((data) => {
  loading.value = true;
  data["linkedResourceType"] = "feedback";
  UserService.postFeedbackComment(data, currFeedback.value.id).then(
    (response) => {
      msg.value = response.message;
      successful.value = true;
      showToast("Comment posted successfully.");
      isVisible.value = false;
      loading.value = false;
    },
    (error) => {
      msg.value =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();
      successful.value = false;
      loading.value = false;
    }
  );
});

// Fetch feedback on component mount
getUserFeedback(currentUser.id);
</script>

<style scoped>
.emoji {
  border: none;
  margin: 0.5rem;
  font-size: 1.5rem;
  padding: 0;
  background: none;
  filter: grayscale(1);
  cursor: pointer;
}

.emoji.selected {
  filter: grayscale(0);
  /* Optional: Add more styles for better visibility */
  border: 2px solid #1a73e87a;
  border-radius: 5px;
}

.size-counter {
  position: relative;
  font-weight: lighter;
  text-align: right;
  color: #454545;
}

.leave-comment {
  cursor: pointer;
  color: #1a73e8;
  font-weight: bold;
}

.dark-text-color {
  color: rgba(15, 20, 25, 1);
}

.comment-card {
  padding: 5px;
  margin-bottom: 0;
  border-radius: 8px;
}

.v-enter-active {
  transition: opacity 0.4s linear;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}

@media screen and (max-width: 768px) {
  .emoji {
    margin: 0.25rem;
    font-size: 1rem;
  }
  .card {
    margin-top: 10px;
  }
  .card-container.card {
    max-width: 90% !important;
    padding: 10px 10px;
  }
}
</style>
